import { useApolloClient } from "@apollo/client";
import {
  RoomPageQuery,
  RoomPageQueryVariables,
  useDeleteRoomMutation,
  useUpdateRoomMutation,
} from "src/generated/graphql";
import {
  Text,
  Badge,
  Button,
  Title,
  MantineTheme,
  useMantineTheme,
} from "@mantine/core";
import { FetchParams, QueryResponse } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useCallback, useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";
import { GetRoomPage } from "src/api/graphql/queries/Rooms";
import { useModals } from "@mantine/modals";
import CreateRoomForm from "../components/CreateModal";
import ActionRoomModalForm from "../components/ActionModal";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import { DEFAULT_ORDER, EMPLOYEE_ROLE_OBJ } from "src/utils/constants";
import useWritePermission from "src/hooks/usePermission";
import useCustomModal from "src/hooks/useCustomModal";

export type RoomData = ArrayElement<RoomPageQuery["roomPage"]["items"]>;

const RoomList = () => {
  const modals = useModals();
  const { setCurrentPageTitle, currentLocation } = useContext(AppContext);
  const { query } = useApolloClient();
  const theme = useMantineTheme();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.locRooms);
  const {
    refetchTableData,
    setrefetchTableData,
    ShowActiveStatus,
    GridOptions,
  } = useCustomModal();

  // Query locationServices here
  const getRooms = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<RoomData>> => {
    try {
      let { data } = await query<RoomPageQuery, RoomPageQueryVariables>({
        query: GetRoomPage,
        variables: {
          pagination: {
            count: fetchParams.count,
            page: fetchParams.page,
            orderBy: fetchParams.orderBy || DEFAULT_ORDER.name,
            filters: fetchParams.filters,
          },
          activeLocationId: currentLocation?.id,
        },
      });

      if (data.roomPage) {
        return {
          rowCount: data.roomPage.totalRows,
          rowData: data.roomPage.items as RoomData[],
        };
      }
      return {
        rowData: [],
        rowCount: 0,
      };
    } catch (e) {
      new Error();
      return {
        rowData: [],
        rowCount: 0,
      };
    }
  };

  useEffect(() => {
    setCurrentPageTitle("Room List");

    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const handleCreateModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create a Room",
      children: (
        <CreateRoomForm
          refetchData={setrefetchTableData}
          handleClose={modals.closeAll}
        />
      ),
    });
  }, [modals]);

  const handleActionModal = useCallback(
    (data: RoomData) => {
      modals.openModal({
        closeOnClickOutside: false,
        title: "Room Details",
        children: (
          <ActionRoomModalForm
            refetchData={setrefetchTableData}
            handleClose={modals.closeAll}
            roomData={data}
            isAllowWrite={isAllowWrite}
          />
        ),
      });
    },
    [modals, isAllowWrite]
  );

  const [deleteRoom] = useDeleteRoomMutation({
    onCompleted: () => {
      setrefetchTableData(!refetchTableData);
    },
  });
  const [updateRoom] = useUpdateRoomMutation({
    onCompleted: () => {
      setrefetchTableData(!refetchTableData);
    },
  });

  const onDelete = (id: string) => {
    deleteRoom({ variables: { deleteRoomId: id! } });
  };
  const onUpdate = (id: string, isActive: boolean) => {
    updateRoom({ variables: { updateRoomId: id, data: { isActive } } });
  };

  const ShowTable = useCallback(
    ({ theme }: { theme: MantineTheme }) => {
      return (
        <div>
          <ScrollableDataGrid<RoomData>
            topMargin={56}
            columnConfigs={[
              {
                field: "name",
                name: "Name",
                filter: {
                  field: "name",
                  type: "text",
                },
                sortable: true,
                cellRenderer: (rowItem) => (
                  <Text
                    weight={500}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleActionModal(rowItem)}
                  >
                    {rowItem.name}
                  </Text>
                ),
              },
              {
                field: "totalCapacity",
                name: "Total Capacity",
                filter: {
                  field: "totalCapacity",
                  type: "text",
                },
                sortable: true,
                cellRenderer: (rowItem) => <Text>{rowItem.totalCapacity}</Text>,
              },
              {
                field: "services",
                name: "Supported Services",
                cellRenderer: (rowItem) =>
                  rowItem.services.map((service) => (
                    <Badge id={service.id} key={service.id} mr={4}>
                      {service.organizationService.name}
                    </Badge>
                  )),
              },
              {
                field: "isActive",
                name: "Active",
                sortable: true,
                cellRenderer: ({ isActive }) => ShowActiveStatus(isActive),
              },
              ...(isAllowWrite
                ? [
                    {
                      field: "",
                      name: "",
                      cellRenderer: ({ id, isActive }: RoomData) => (
                        <GridOptions
                          id={id}
                          onUpdate={onUpdate}
                          isActive={isActive}
                          onDelete={onDelete}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            dataFetch={getRooms}
          />
        </div>
      );
    },
    [currentLocation, refetchTableData, isAllowWrite]
  );

  if (!currentLocation?.id)
    return <Title order={3}>Please Select Location from Header</Title>;
  return (
    <>
      {isAllowWrite && (
        <Button style={{ marginBottom: 20 }} onClick={handleCreateModal}>
          Create +
        </Button>
      )}
      <ShowTable theme={theme} />
    </>
  );
};

export default RoomList;
